import { format, getYear, subYears } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { enUS, zhCN, ko } from 'date-fns/locale';
import { Popper, Input, ClickAwayListener } from '@mui/base';
import { styled } from '@mui/system';

import { DayPicker, DayPickerSingleProps } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import './datepicker.css';
const SingleDatePickerRoot = styled('div', {
  name: 'SingleDatePickerRoot',
  slot: 'Root',
})((props) => ({}));

const CalenderButton = styled('div')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: inherit;
  cursor: pointer;
  position: absolute;
  top: 0;
  height: 100%;
  right: 14px;
`;

const StyledInputElement = styled('input')`
  width: 100%;
`;

const StyledInputRoot = styled('div')`
  position: relative;
`;

interface SingleDatePickerProps {
  lang?: string;
  singleDate: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: DayPickerSingleProps['disabled'];
  enableMonthSelection?: boolean;
}

const SingleDatePicker = (props: SingleDatePickerProps) => {
  const { lang, singleDate = '', onChange, placeholder, enableMonthSelection } = props;

  const [isOpen, setIsOpen] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selected, setSelected] = useState<Date>();

  const id = isOpen ? 'calender-popper' : undefined;
  const defaultDateFormat = 'y-MM-dd';

  // Only accept digits and forward slash as input.
  const onInputChange = (input) => {
    onChange(input.trim().replace(/[^\d/]+/g, ''));
  };

  // When the input field loses focus, we need to parse
  // the input to set the date. While doing this, we also do some
  // assumptions for the user and fix mistakes.
  // const onInputBlur = () => {
  //   // If the input is empty, we should just go ahead and
  //   // clear the current selection.
  //   if (singleDate === '') {
  //     // clearSelected();
  //     return;
  //   }
  // };

  const handleClickAway = () => {
    setIsOpen(false);
  };

  const handleDaySelect = (date: Date) => {
    setSelected(date);
    if (date) {
      onChange(format(date, defaultDateFormat));
      handleClickAway();
    } else {
      onChange('');
    }
  };

  let caption = {};
  if (enableMonthSelection) {
    caption = {
      captionLayout: 'dropdown-buttons',
      fromYear: subYears(new Date(), 15).getFullYear(),
      toYear: getYear(new Date()),
    };
  }

  const getLang = (lang) => {
    switch (lang) {
      case 'en':
        return enUS;
      case 'zh':
        return zhCN;
      case 'kr':
        return ko;
      default:
        return enUS;
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <SingleDatePickerRoot>
        <Input
          endAdornment={
            <CalenderButton
              onClick={() => {
                setIsOpen(!isOpen);
              }}
            >
              <span className="icon-24-calendar-mono"></span>
            </CalenderButton>
          }
          slotProps={{ input: { className: `c-input ${props.className ?? ''}` } }}
          slots={{
            root: StyledInputRoot,
            input: StyledInputElement,
          }}
          // onBlur={() => onInputBlur()}
          onChange={(e) => onInputChange(e.target.value)}
          placeholder={placeholder || ''}
          value={singleDate}
          ref={(elm) => setAnchorEl(elm)}
        />
        <Popper id={id} open={isOpen} anchorEl={anchorEl} placement="bottom">
          <div tabIndex={-1} role="dialog" aria-label="DayPicker calendar" className="dialog-sheet">
            <DayPicker
              locale={getLang(lang)}
              {...caption}
              disabled={props.disabled}
              mode="single"
              selected={selected}
              onSelect={handleDaySelect}
            />
          </div>
        </Popper>
      </SingleDatePickerRoot>
    </ClickAwayListener>
  );
};

export default SingleDatePicker;
